import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OTTERFOLIO_THEME } from "./theme";

export interface OverridesForm {
  growthEstimatePercent: number | null;
  payoutYieldPercent: number | null;
  valuationDeltaPercent: number | null;
}

interface Props {
  open: boolean;
  initial: OverridesForm;
  onClose: () => void;
  onSave: (v: OverridesForm) => Promise<void>;
}

function toNumOrNull(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function RenditebrueckeEditModal({ open, initial, onClose, onSave }: Props) {
  const { t } = useTranslation();
  const [growth, setGrowth] = useState(initial.growthEstimatePercent?.toString() ?? "");
  const [payout, setPayout] = useState(initial.payoutYieldPercent?.toString() ?? "");
  const [valuation, setValuation] = useState(initial.valuationDeltaPercent?.toString() ?? "");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        growthEstimatePercent: toNumOrNull(growth),
        payoutYieldPercent: toNumOrNull(payout),
        valuationDeltaPercent: toNumOrNull(valuation),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: overlay dialog uses controlled visibility
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: OTTERFOLIO_THEME.bg,
          border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
          padding: 20,
          borderRadius: 12,
          minWidth: 340,
          boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          color: OTTERFOLIO_THEME.text,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16 }}>
          {t("otterfolio.renditebruecke.overrides", { defaultValue: "Deine Werte" })}
        </h2>
        <label style={{ fontSize: 12 }}>
          {t("otterfolio.renditebruecke.growthEstimateLabel", { defaultValue: "Gewinnwachstum (%/a)" })}
          <input
            value={growth}
            onChange={(e) => setGrowth(e.target.value)}
            inputMode="decimal"
            placeholder="z.B. 8"
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          {t("otterfolio.renditebruecke.payoutYieldLabel", { defaultValue: "Ausschüttung (Div + Buyback, %/a)" })}
          <input
            value={payout}
            onChange={(e) => setPayout(e.target.value)}
            inputMode="decimal"
            placeholder="z.B. 3.5"
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          {t("otterfolio.renditebruecke.valuationDeltaLabel", { defaultValue: "Bewertungs-Delta (%/a)" })}
          <input
            value={valuation}
            onChange={(e) => setValuation(e.target.value)}
            inputMode="decimal"
            placeholder="z.B. -1.5 oder 2"
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel", { defaultValue: "Abbrechen" })}
          </button>
          <button type="button" onClick={save} disabled={busy}>
            {t("common.save", { defaultValue: "Speichern" })}
          </button>
        </div>
      </div>
    </div>
  );
}
